import { memo } from 'react'
import { type SkeletonProps } from '@/features/shared/components/SkeletonLoader/types'
import { range } from '@/features/shared/utils/range'
import { Skeleton } from '@shipt/design-system-loading'
import styled from 'styled-components'

type InferMode = {
  /**
   * when true, children render invisibly (via `visibility: hidden`) to provide
   * natural dimensions for the skeleton overlay. use this when the skeleton
   * needs to match the exact size of its content (e.g., a price label whose
   * width varies). children are hidden from assistive tech via `aria-hidden`.
   *
   * note: children are still mounted and their effects/logic will run.
   * this is intentional, `inferSize` should be opted into explicitly for
   * short lived loading states (e.g., cart mutations, refetches) where
   * the content is already rendered and only temporarily masked.
   */
  inferSize: true
  width?: never
  height?: never
}

type ExplicitMode = {
  inferSize?: false
  width?: SkeletonProps['width']
  height?: SkeletonProps['height']
}

export type SkeletonLoaderProps = Omit<SkeletonProps, 'width' | 'height'> &
  (InferMode | ExplicitMode)

/**
 The presentational component that can be used independently
 */
export const SkeletonLoader = memo(
  ({
    isLoading,
    children,
    className = '',
    surface = 'default',
    animation = true,
    variant,
    ...sizeProps
  }: SkeletonLoaderProps) => {
    if (!isLoading) return children ? <>{children}</> : null

    if ('inferSize' in sizeProps && sizeProps.inferSize) {
      return (
        <Wrapper className={className}>
          <HiddenContent aria-hidden="true">{children}</HiddenContent>
          <OverlaySkeleton animation={animation} surface={surface} variant={variant} />
        </Wrapper>
      )
    }
    return (
      <Skeleton
        animation={animation}
        className={className}
        width={sizeProps.width}
        height={sizeProps.height}
        surface={surface}
        variant={variant}
      />
    )
  },
)

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const HiddenContent = styled.div`
  visibility: hidden;
  pointer-events: none;
`

const OverlaySkeleton = styled(Skeleton)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`

type SkeletonLoadersProps = SkeletonLoaderProps & { count: number }

export const Skeletons = ({ count, ...restProps }: SkeletonLoadersProps) => (
  <>
    {range(count).map((v) => (
      <SkeletonLoader {...restProps} key={v} />
    ))}
  </>
)
